"use client";

import { useMemo } from "react";
import { ICM_BLOGS_URL } from "@/app/utils/constants";
import GrowWealthChevron from "@/app/assets/icons/grow-wealth/chevron-right";
import {
    ArrowUpRightIcon,
    BookOpenTextIcon,
    CalendarDaysIcon,
    MetaDot,
} from "@/app/assets/icons/from-the-blog/icons";
import type { FromTheBlogPost } from "@/app/components/icm 3.0/from-the-blog-data";
import type { BlogData } from "@/app/interface/homepage";
import { useRemoteConfig } from "@/app/hooks/use-remote-config";

const DESKTOP_COUNT = 6;
const MOBILE_COUNT = 3;

const readMoreClass =
    "inline-flex items-center justify-center gap-2 rounded-[var(--radius-m)] border border-cta-secondary-stroke bg-cta-secondary px-6 py-2.5 font-medium text-cta-secondary-text no-underline transition-colors hover:border-cta-secondary-stroke-hover hover:bg-cta-secondary-hover hover:text-cta-secondary-text-hover focus:border-cta-secondary-stroke-focus focus:bg-cta-secondary-focus focus:text-cta-secondary-text-focus";

/** Map Firebase remote-config blog payload to homepage card fields. */
function mapBlogData(item: BlogData): FromTheBlogPost {
    return {
        title: item.title ?? "",
        description: item.description ?? "",
        // Remote config has no category; keep pill for layout parity.
        category: "Blog",
        // Same fields as platform FromTheBlog / blogs-section (label2 = date, label1 = author/meta).
        date: item.label2 ?? "",
        readTime: item.label1 ?? "",
        blogUrl: item.blogUrl ?? ICM_BLOGS_URL,
    };
}

const CategoryPill = ({ label }: { label: string }) => (
    <span className="inline-flex items-center justify-center rounded-[8px] border border-solid border-[#FDE7D7] bg-[#FEF5EE] px-s6 py-s3 text-sm font-medium leading-5 text-[#F15D2A]">
        {label}
    </span>
);

const MetaRow = ({ date, readTime }: { date: string; readTime: string }) => (
    <div className="flex items-center gap-3">
        {date ? (
            <div className="flex items-center gap-2">
                <CalendarDaysIcon />
                <span className="whitespace-nowrap text-sm font-regular leading-5 text-[#575757] md:text-base md:leading-[22px]">
                    {date}
                </span>
            </div>
        ) : null}
        {date && readTime ? <MetaDot /> : null}
        {readTime ? (
            <div className="flex items-center gap-2">
                <BookOpenTextIcon />
                <span className="whitespace-nowrap text-sm font-regular leading-5 text-[#575757] md:text-base md:leading-[22px]">
                    {readTime}
                </span>
            </div>
        ) : null}
    </div>
);

const BlogCard = ({ blog }: { blog: FromTheBlogPost }) => (
    <a
        href={blog.blogUrl || ICM_BLOGS_URL}
        className="flex w-full items-center rounded-2xl bg-white p-4 no-underline md:flex-1"
    >
        <div className="flex w-full min-w-0 flex-col gap-6">
            <span className="md:hidden">
                <CategoryPill label={blog.category} />
            </span>
            <div className="hidden items-center justify-between md:flex">
                <MetaRow date={blog.date} readTime={blog.readTime} />
                <CategoryPill label={blog.category} />
            </div>

            <div className="flex w-full min-w-0 flex-col gap-4 md:gap-6">
                <div className="md:hidden">
                    <MetaRow date={blog.date} readTime={blog.readTime} />
                </div>

                <div className="flex w-full min-w-0 flex-col gap-2">
                    <div className="flex items-center justify-between gap-3">
                        <h3 className="min-w-0 truncate text-base font-bold leading-[22px] text-black md:text-xl md:leading-[26px]">
                            {blog.title}
                        </h3>
                        <span className="flex shrink-0 flex-col items-start pt-1">
                            <ArrowUpRightIcon />
                        </span>
                    </div>
                    {blog.description ? (
                        <p className="text-sm font-regular leading-5 text-[var(--colors-text-body-text-1,#767676)] md:max-w-[432px] md:text-base md:leading-[22px]">
                            {blog.description}
                        </p>
                    ) : null}
                </div>
            </div>
        </div>
    </a>
);

const BlogCardSkeleton = () => (
    <div className="flex w-full animate-pulse rounded-2xl bg-white p-4 md:flex-1">
        <div className="flex w-full flex-col gap-6">
            <div className="h-7 w-20 rounded-lg bg-gray-200" />
            <div className="flex flex-col gap-2">
                <div className="h-6 w-3/4 rounded bg-gray-200" />
                <div className="h-4 w-full rounded bg-gray-200" />
                <div className="h-4 w-2/3 rounded bg-gray-200" />
            </div>
        </div>
    </div>
);

/**
 * From the Blog — Figma desktop 4481:49365 / mobile 4481:49701
 * Posts come from Firebase remote config key `blogs` (same as platform FromTheBlog).
 */
const FromTheBlog = () => {
    const { data: blogData, loading: isLoading, error } = useRemoteConfig<BlogData[]>(
        "blogs",
        [],
    );

    const posts = useMemo(
        () => (blogData ?? []).map(mapBlogData).filter((post) => Boolean(post.title)),
        [blogData],
    );
    const desktopBlogs = posts.slice(0, DESKTOP_COUNT);
    const mobileBlogs = posts.slice(0, MOBILE_COUNT);

    return (
        <section
            aria-labelledby="from-the-blog-heading"
            className="mt-10 w-full bg-white px-4 pb-10 md:mt-[120px] md:px-[120px] md:pb-0"
        >
            <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-6 md:gap-[60px]">
                <header className="flex w-full flex-col items-start md:h-[92px] md:flex-row md:items-center md:justify-between">
                    <div className="flex w-full flex-col gap-1 md:max-w-[728px] md:gap-2">
                        <p className="w-full text-base font-semibold uppercase leading-[22px] tracking-[2px] text-brand-primary-500 md:text-xl md:leading-[26px]">
                            From the Blog
                        </p>
                        <h2
                            id="from-the-blog-heading"
                            className="w-full text-2xl font-bold leading-8 text-black md:max-w-[715px] md:text-5xl md:leading-[58px]"
                        >
                            Insights for smarter investing.
                        </h2>
                    </div>

                    <a
                        href={ICM_BLOGS_URL}
                        className={`hidden h-10 shrink-0 text-base leading-[22px] md:inline-flex ${readMoreClass}`}
                    >
                        Read More
                        <GrowWealthChevron />
                    </a>
                </header>

                {error ? (
                    <div className="w-full rounded-2xl bg-[#F5F5F5] p-5 text-center text-red-500">
                        Something went wrong
                    </div>
                ) : (
                    <div className="w-full overflow-hidden rounded-2xl bg-[#F5F5F5] p-5">
                        {isLoading ? (
                            <>
                                <ul className="hidden gap-5 md:grid md:grid-cols-2">
                                    {Array.from({ length: DESKTOP_COUNT }).map((_, index) => (
                                        <li key={`sk-d-${index}`} className="flex">
                                            <BlogCardSkeleton />
                                        </li>
                                    ))}
                                </ul>
                                <ul className="flex flex-col gap-5 md:hidden">
                                    {Array.from({ length: MOBILE_COUNT }).map((_, index) => (
                                        <li key={`sk-m-${index}`}>
                                            <BlogCardSkeleton />
                                        </li>
                                    ))}
                                </ul>
                            </>
                        ) : (
                            <>
                                <ul className="hidden gap-5 md:grid md:grid-cols-2">
                                    {desktopBlogs.map((blog) => (
                                        <li key={blog.blogUrl} className="flex">
                                            <BlogCard blog={blog} />
                                        </li>
                                    ))}
                                </ul>
                                <ul className="flex flex-col gap-5 md:hidden">
                                    {mobileBlogs.map((blog) => (
                                        <li key={`${blog.blogUrl}-m`}>
                                            <BlogCard blog={blog} />
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>
                )}

                <a
                    href={ICM_BLOGS_URL}
                    className={`w-full text-sm leading-5 md:hidden ${readMoreClass}`}
                >
                    Read More
                    <GrowWealthChevron />
                </a>
            </div>
        </section>
    );
};

export default FromTheBlog;
